/**
 * The allergen filter of the card on the net (F29, docs/15-getraenkekarte.md, Allergenfilter), after
 * SpeiseKarte's (S63, allergen-filter.ts there): guests tick allergens and the drinks with them are
 * gone, by CSS alone (`:has()`), without a script and without anything sent or kept. Here the rules:
 * which of the 14 a drink contains, and when a group, a section or a whole part is gone with them.
 *
 * Conditions are kept as a disjunction of conjunctions: a list of sets of letters, each set „all of
 * these ticked“, the list „any of these“. A drink with gluten and milk is gone at [["a"], ["g"]]; a
 * group of it and a drink with only sulphites at [["a", "l"], ["g", "l"]]. [] is „never“.
 */
import { ALLERGENS } from "@household-kit/food";
import { allergenId } from "./drinks.ts";
import type { CardEntry, CardPart } from "./drinks-card.ts";

/** The 14 of the LMIV by their letters, in the order of annex II. */
export const FILTER_LETTERS = ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n"] as const;

const LETTER = new Map(ALLERGENS.map((a) => [a.id, a.code]));

/** The letter of one of the 14 („Sulfite“ → l), however it is written; null for anything else. */
export const filterLetter = (allergen: string): string | null => LETTER.get(allergenId(allergen) ?? "") ?? null;

/** The id of household-kit `food` for a letter („l“ → sulphites). */
export const letterAllergen = (letter: string): string => ALLERGENS.find((a) => a.code === letter)!.id;

/**
 * The letters of a drink, in the order a–n; null when its allergens are not kept up: none on it and
 * not ticked „enthält keine der 14“ (F29, Martin 04.11.2026). A drink with only other allergens
 * („Hopfen“) is kept up and has none of the 14.
 */
export function entryLetters(e: Pick<CardEntry, "allergens" | "allergenFree">): string[] | null {
  const allergens = e.allergens ?? [];
  if (!allergens.length && e.allergenFree !== true) return null;
  const set = new Set(allergens.map(filterLetter).filter((l): l is string => l !== null));
  return FILTER_LETTERS.filter((l) => set.has(l));
}

/** The letters on a card, in the order a–n: the boxes of the filter. */
export function cardLetters(parts: readonly CardPart[]): string[] {
  const on = new Set(parts.flatMap((p) => p.sections.flatMap((s) => s.groups.flatMap((g) => g.entries.flatMap((e) => entryLetters(e) ?? [])))));
  return FILTER_LETTERS.filter((l) => on.has(l));
}

/** A condition: any of these sets of letters, each wholly ticked. */
export type Gone = string[][];

/** More terms than this and the condition counts as „never“: the heading stays (its drinks still go). */
export const MAX_TERMS = 64;

const key = (t: readonly string[]) => t.join("");
const subset = (a: readonly string[], b: readonly string[]) => a.every((x) => b.includes(x));

/** The smallest terms only, sorted; above MAX_TERMS „never“. */
function minimal(terms: string[][]): Gone {
  const sorted = [...new Map(terms.map((t) => [key(t), [...new Set(t)].sort()])).values()].sort((a, b) => a.length - b.length || key(a).localeCompare(key(b)));
  const out: string[][] = [];
  for (const t of sorted) if (!out.some((o) => subset(o, t))) out.push(t);
  return out.length > MAX_TERMS ? [] : out;
}

/** Gone when each is gone; [] when one of them is never gone. */
export function allOf(...cs: Gone[]): Gone {
  if (!cs.length || cs.some((c) => c.length === 0)) return [];
  let acc: string[][] = cs[0]!;
  for (const c of cs.slice(1)) {
    const next: string[][] = [];
    for (const a of acc) for (const b of c) next.push([...a, ...b]);
    acc = minimal(next);
    if (!acc.length) return [];
  }
  return minimal(acc);
}

/** A drink: gone with any one of its letters; never when it has none or is not kept up. */
export const entryGone = (e: Pick<CardEntry, "allergens" | "allergenFree">): Gone => (entryLetters(e) ?? []).map((l) => [l]);

/** A group, a section or a part: gone when every drink in it is. */
export const entriesGone = (entries: readonly Pick<CardEntry, "allergens" | "allergenFree">[]): Gone => (entries.length ? allOf(...entries.map(entryGone)) : []);

/** The CSS of one condition: `body:has(#af-a:checked):has(#af-g:checked) <target>`, one selector per term. */
export function goneCss(c: Gone, target: string): string {
  if (!c.length) return "";
  return `${c.map((t) => `body${t.map((l) => `:has(#af-${l}:checked)`).join("")} ${target}`).join(", ")} { display: none; }`;
}
